// Pre-write `~/.gitnexus/config.json` để chạy `gitnexus wiki` mà KHÔNG phải pass
// `--api-key` qua CLI argument list. Lý do: argv leak qua `ps aux` / `/proc/<pid>/cmdline`
// cho mọi process cùng user trong suốt wiki run (timeout 5 phút) — secret exposure cao.
//
// Gitnexus binary tự đọc config từ `~/.gitnexus/config.json` khi không có `--api-key`
// (xác minh qua `gitnexus wiki --help`: "--api-key ... saved to ~/.gitnexus/config.json").
//
// Approach: idempotent merge — read existing config (preserve user fields khác), update
// `apiKey/baseUrl/model/isReasoningModel`, write back với chmod 600.
//
// v1.13.0 security fix CVE-internal: gitnexus CLI arg API key leak.

import { promises as fs } from "node:fs";
import { homedir } from "node:os";
import { dirname, join } from "node:path";

const GITNEXUS_CONFIG_DIR = join(homedir(), ".gitnexus");
const GITNEXUS_CONFIG_PATH = join(GITNEXUS_CONFIG_DIR, "config.json");

export interface GitnexusConfigPayload {
  apiKey: string;
  baseUrl: string;
  model: string;
  isReasoningModel?: boolean;
}

// Read existing config nếu có. Lỗi parse / file missing → trả empty object để merge.
async function readExistingConfig(): Promise<Record<string, unknown>> {
  try {
    const raw = await fs.readFile(GITNEXUS_CONFIG_PATH, "utf8");
    const parsed = JSON.parse(raw);
    return typeof parsed === "object" && parsed !== null ? (parsed as Record<string, unknown>) : {};
  } catch {
    return {};
  }
}

// Atomic write: tmp file + rename để tránh corrupt config nếu process crash giữa chừng.
async function writeConfigAtomic(content: string): Promise<void> {
  await fs.mkdir(dirname(GITNEXUS_CONFIG_PATH), { recursive: true });
  const tmpPath = `${GITNEXUS_CONFIG_PATH}.tmp-${process.pid}`;
  await fs.writeFile(tmpPath, content, { mode: 0o600 });
  await fs.rename(tmpPath, GITNEXUS_CONFIG_PATH);
}

// Main API: merge payload vào existing config, write atomic, chmod 600.
// Caller (run-gitnexus-wiki-conditional.ts) gọi function này TRƯỚC khi spawn
// `gitnexus wiki` mà không kèm `--api-key`.
export async function writeGitnexusConfigForWikiRun(payload: GitnexusConfigPayload): Promise<void> {
  const existing = await readExistingConfig();
  const merged = {
    ...existing,
    apiKey: payload.apiKey,
    baseUrl: payload.baseUrl,
    model: payload.model,
    isReasoningModel: payload.isReasoningModel ?? false,
  };
  await writeConfigAtomic(JSON.stringify(merged, null, 2));
  // Defensive: ensure 600 dù tmp file đã chmod (some FS lose mode on rename).
  await fs.chmod(GITNEXUS_CONFIG_PATH, 0o600).catch(() => {
    // Best-effort — Windows / WSL ignore chmod, mode đã set ở writeFile.
  });
}
